/**
 * IncomingCallLeadPicker — shown inside IncomingCallBanner when several leads
 * share the caller's number. Collapsed it only says how many others there
 * are; expanded it lists them (best first, as the backend ranks them) and
 * picking one makes the call answer as that lead.
 */
import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import type { TLeadPhoneMatch } from "@/models/leads.model";
import { leadMatchName } from "@/context/inbound-lead-matches";

type Props = {
  matches: TLeadPhoneMatch[];
  total: number;
  selectedId: number | null;
  onSelect: (leadId: number) => void;
};

const IncomingCallLeadPicker = ({ matches, total, selectedId, onSelect }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const others = total - 1;

  return (
    <div className="mt-2.5">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        className="inline-flex items-center gap-1 text-[11.5px] font-medium text-amber-300 transition hover:text-amber-200"
      >
        +{others} more {others === 1 ? "lead" : "leads"} with this number
        <ChevronDown className={`h-3.5 w-3.5 transition ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="mt-2 max-h-44 space-y-1 overflow-y-auto pr-1" role="listbox" aria-label="Leads with this number">
          {matches.map((lead) => {
            const selected = lead.id === selectedId;
            const details = [lead.ownerName ?? "Unassigned", lead.statusName].filter(Boolean).join(" · ");
            return (
              <button
                key={lead.id}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => onSelect(lead.id)}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left transition ${
                  selected ? "bg-brand-500/15 ring-1 ring-brand-400/40" : "hover:bg-white/[0.05]"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12.5px] font-medium text-white">
                    {leadMatchName(lead) ?? "Unnamed lead"}
                  </p>
                  <p className="truncate text-[11px] text-slate-400">{details}</p>
                </div>
                {selected && <Check className="h-3.5 w-3.5 shrink-0 text-brand-300" />}
              </button>
            );
          })}
          {total > matches.length && (
            <p className="px-2.5 pt-1 text-[10.5px] text-slate-500">
              Showing the {matches.length} most relevant of {total}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default IncomingCallLeadPicker;
